/**
 * Slice 19c — when the rep picks a template in manual ingest, auto-add the
 * template's archetype tags to the selected post tag set (additive only).
 *
 * - Never removes a user-picked tag.
 * - Never re-applies the same template's tags twice (debounced per templateId).
 * - Respects the 5-tag cap by skipping ids that would push over the cap.
 * - No-op when templateId is empty or the template has no archetype tags.
 */
import { useEffect, useRef } from "react";
import type { AdsAssistantTemplateRow } from "@/lib/adsAssistantClient";

const MAX_POST_TAGS = 5;

export function useTemplateArchetypeTagInherit(args: {
  templateId: string;
  templates: AdsAssistantTemplateRow[];
  selectedPostTags: Set<string>;
  setSelectedPostTags: React.Dispatch<React.SetStateAction<Set<string>>>;
}) {
  const { templateId, templates, selectedPostTags, setSelectedPostTags } = args;
  /** Last templateId we already auto-applied (so changing template re-applies new set). */
  const lastAppliedRef = useRef<string>("");

  useEffect(() => {
    if (!templateId) {
      lastAppliedRef.current = "";
      return;
    }
    if (lastAppliedRef.current === templateId) return;

    const tpl = templates.find((t) => t.id === templateId);
    const ids = Array.isArray(tpl?.archetype_tag_ids) ? tpl.archetype_tag_ids : [];
    lastAppliedRef.current = templateId;
    if (!ids.length) return;

    setSelectedPostTags((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (next.size >= MAX_POST_TAGS) break;
        if (!next.has(id)) next.add(id);
      }
      return next.size === prev.size ? prev : next;
    });
    // selectedPostTags is intentionally NOT a dep — we read it via the setter
    // to keep this additive and avoid re-runs on every tag toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, templates]);

  // Silence lint if a future eslint config flags the captured `selectedPostTags`.
  void selectedPostTags;
}
